/**
 * Abono de precio fijo (`discounts.fixed_price`).
 *
 * Cada sesión del abono tiene su propia entrada («Abono - nombre») con un precio
 * por nivel del mapa. El abono trae esos precios en `pass_prices` (uno por
 * sesión y nivel; `level_id` null en los eventos sin mapa) y su resumen en
 * `price_summary`. Un nivel sin precio no entra en el abono en esa sesión.
 * El back es quien manda: pone la entrada del abono en cada línea y rechaza las
 * butacas de niveles sin precio. Aquí solo se usa para enseñar precios y para no
 * dejar elegir una butaca que no entra en el abono.
 */

export function isFixedPricePass(pass: any): boolean {
  return !!pass?.fixed_price;
}

/** Precio del abono en una sesión y nivel (`levelId` = PK de map_levels; null sin mapa), o null si no entra. */
export function fixedPassPrice(pass: any, sessionId: number, levelId: number | null): number | null {
  const row = fixedPassPriceRow(pass, sessionId, levelId);
  return row ? +row.price : null;
}

export function fixedPassPriceRow(pass: any, sessionId: number, levelId: number | null): any | null {
  return (pass?.pass_prices || []).find((p: any) =>
    +p.session_id === +sessionId && (levelId == null ? p.level_id == null : +p.level_id === +levelId)
  ) || null;
}

/** Lo más barato que puede costar el abono, o null si el back no lo trae. */
export function fixedPassFrom(pass: any): number | null {
  const from = pass?.price_summary?.from;
  return from == null ? null : +from;
}

/** Texto corto para tarjetas y cabeceras en lugar del "-X %": «Desde 34,00 €». */
export function fixedPassFromLabel(pass: any): string {
  const from = fixedPassFrom(pass);
  if (from == null) return 'Precio fijo';
  return `Desde ${from.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

/**
 * Entrada que se enseña en un abono de precio fijo: la del abono en esa sesión,
 * con su nombre y el precio del nivel. El back vuelve a resolverla al añadir.
 */
export function fixedPassTicket(pass: any, sessionId: number, levelId: number | null): any | null {
  const row = fixedPassPriceRow(pass, sessionId, levelId);
  if (!row) return null;
  return {
    id: row.ticket_id ?? null,
    name: pass?.public_name || pass?.name || 'Entrada del abono',
    price: +row.price,
    active: true,
    max_tickets: null,
    fixed_pass_price: true,
  };
}
